import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { pinsApi, PinData } from '../services/pinsApi';

const MAX_PINNED_COLLECTIONS = 5;
const MAX_PINNED_PROBLEMS_PER_COLLECTION = 10;

export const usePinManagement = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const [pinData, setPinData] = useState<PinData>({
    pinnedCollections: [],
    pinnedProblems: {},
    lastUpdated: Date.now()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's pin data from API
  useEffect(() => {
    if (!isAuthenticated || !currentUser) {
      setPinData({
        pinnedCollections: [],
        pinnedProblems: {},
        lastUpdated: Date.now()
      });
      return;
    }

    const fetchPins = async () => {
      try {
        setLoading(true);
        console.log('[PIN] Fetching pins for user:', currentUser.uid);
        const data = await pinsApi.getPins();
        console.log('[PIN] Received pin data:', data);
        setPinData(data);
      } catch (err) {
        console.error('[PIN] Error fetching pins:', err);
        setError('Failed to load pins');
        // Initialize empty pin data on error
        setPinData({
          pinnedCollections: [],
          pinnedProblems: {},
          lastUpdated: Date.now()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPins();
  }, [currentUser, isAuthenticated]);

  // Pin a collection
  const pinCollection = useCallback(async (collectionId: string): Promise<{success: boolean; message?: string}> => {
    if (!currentUser) {
      return { success: false, message: 'You must be logged in to pin collections' };
    }

    // Check if already pinned
    if (pinData.pinnedCollections.includes(collectionId)) {
      return { success: false, message: 'Collection is already pinned' };
    }

    // Check max limit
    if (pinData.pinnedCollections.length >= MAX_PINNED_COLLECTIONS) {
      return { success: false, message: `Maximum ${MAX_PINNED_COLLECTIONS} collections can be pinned` };
    }

    try {
      console.log('[PIN] Pinning collection:', collectionId);
      const updatedData = await pinsApi.pinCollection(collectionId);
      console.log('[PIN] Pin successful, updated data:', updatedData);
      setPinData(updatedData);
      return { success: true };
    } catch (err) {
      console.error('[PIN] Error pinning collection:', err);
      return { success: false, message: 'Failed to pin collection' };
    }
  }, [currentUser, pinData]);

  // Unpin a collection
  const unpinCollection = useCallback(async (collectionId: string): Promise<{success: boolean; message?: string}> => {
    if (!currentUser) {
      return { success: false, message: 'You must be logged in' };
    }

    try {
      console.log('[PIN] Unpinning collection:', collectionId);
      console.log('[PIN] Current pinData before unpin:', pinData);
      const updatedData = await pinsApi.unpinCollection(collectionId);
      console.log('[PIN] Unpin successful, updated data:', updatedData);
      setPinData(updatedData);
      // Force a small delay to ensure state propagation
      await new Promise(resolve => setTimeout(resolve, 100));
      return { success: true };
    } catch (err) {
      console.error('[PIN] Error unpinning collection:', err);
      return { success: false, message: 'Failed to unpin collection' };
    }
  }, [currentUser, pinData]);

  // Pin a problem
  const pinProblem = useCallback(async (collectionId: string, problemId: string): Promise<{success: boolean; message?: string}> => {
    if (!currentUser) {
      return { success: false, message: 'You must be logged in to pin problems' };
    }

    const collectionPins = pinData.pinnedProblems[collectionId] || [];

    // Check if already pinned
    if (collectionPins.includes(problemId)) {
      return { success: false, message: 'Problem is already pinned' };
    }

    // Check max limit
    if (collectionPins.length >= MAX_PINNED_PROBLEMS_PER_COLLECTION) {
      return { success: false, message: `Maximum ${MAX_PINNED_PROBLEMS_PER_COLLECTION} problems can be pinned per collection` };
    }

    try {
      console.log('[PIN] Pinning problem:', problemId, 'in collection:', collectionId);
      const updatedData = await pinsApi.pinProblem(collectionId, problemId);
      console.log('[PIN] Pin problem successful, updated data:', updatedData);
      setPinData(updatedData);
      return { success: true };
    } catch (err) {
      console.error('[PIN] Error pinning problem:', err);
      return { success: false, message: 'Failed to pin problem' };
    }
  }, [currentUser, pinData]);

  // Unpin a problem
  const unpinProblem = useCallback(async (collectionId: string, problemId: string): Promise<{success: boolean; message?: string}> => {
    if (!currentUser) {
      return { success: false, message: 'You must be logged in' };
    }

    try {
      console.log('[PIN] Unpinning problem:', problemId, 'in collection:', collectionId);
      console.log('[PIN] Current pinData before unpin:', pinData);
      const updatedData = await pinsApi.unpinProblem(collectionId, problemId);
      console.log('[PIN] Unpin problem successful, updated data:', updatedData);
      setPinData(updatedData);
      // Force a small delay to ensure state propagation
      await new Promise(resolve => setTimeout(resolve, 100));
      return { success: true };
    } catch (err) {
      console.error('[PIN] Error unpinning problem:', err);
      return { success: false, message: 'Failed to unpin problem' };
    }
  }, [currentUser, pinData]);

  // Check if collection is pinned
  const isCollectionPinned = useCallback((collectionId: string): boolean => {
    return pinData.pinnedCollections.includes(collectionId);
  }, [pinData]);

  // Check if problem is pinned
  const isProblemPinned = useCallback((collectionId: string, problemId: string): boolean => {
    const collectionPins = pinData.pinnedProblems[collectionId] || [];
    return collectionPins.includes(problemId);
  }, [pinData]);

  // Sort collections with pinned ones first
  const sortCollections = useCallback(<T extends { id: string } | { collection: { id: string } }>(collections: T[]): T[] => {
    return [...collections].sort((a, b) => {
      // Handle both direct id and nested collection.id
      const aId = 'id' in a ? a.id : a.collection.id;
      const bId = 'id' in b ? b.id : b.collection.id;

      const aIsPinned = pinData.pinnedCollections.includes(aId);
      const bIsPinned = pinData.pinnedCollections.includes(bId);

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // If both pinned, sort by pin order (recently pinned first)
      if (aIsPinned && bIsPinned) {
        return pinData.pinnedCollections.indexOf(aId) - pinData.pinnedCollections.indexOf(bId);
      }

      return 0; // Keep original order for unpinned items
    });
  }, [pinData]);

  // Sort problems with pinned ones first
  const sortProblems = useCallback(<T extends { id: string }>(collectionId: string, problems: T[]): T[] => {
    const collectionPins = pinData.pinnedProblems[collectionId] || [];

    return [...problems].sort((a, b) => {
      const aIsPinned = collectionPins.includes(a.id);
      const bIsPinned = collectionPins.includes(b.id);

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // If both pinned, sort by pin order (recently pinned first)
      if (aIsPinned && bIsPinned) {
        return collectionPins.indexOf(a.id) - collectionPins.indexOf(b.id);
      }

      return 0; // Keep original order for unpinned items
    });
  }, [pinData]);

  return {
    pinData,
    loading,
    error,
    pinCollection,
    unpinCollection,
    pinProblem,
    unpinProblem,
    isCollectionPinned,
    isProblemPinned,
    sortCollections,
    sortProblems,
    maxPinnedCollections: MAX_PINNED_COLLECTIONS,
    maxPinnedProblemsPerCollection: MAX_PINNED_PROBLEMS_PER_COLLECTION
  };
};
